import {
    Box,
    Button,
    Typography,
    TextField,
    Alert,
    IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSwipeable } from 'react-swipeable';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/axios';
import BrandModelSelector from '../../widgets/components/BrandModelSelector';

type MediaPreview = {
    type: 'image' | 'video';
    src: string;
};

const PortfolioDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEdit = Boolean(id);

    const [form, setForm] = useState({
        title: '',
        description: '',
        modelIds: [] as string[],
    });

    const [files, setFiles] = useState<File[]>([]);
    const [previews, setPreviews] = useState<MediaPreview[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [deletedMedia, setDeletedMedia] = useState<string[]>([]);

    const [errors, setErrors] = useState<{ title?: string }>({});

    useEffect(() => {
        if (!isEdit) return;

        const fetchItem = async () => {
            try {
                const res = await api.get(`/api/admin/portfolio/${id}`);
                const item = res.data;

                setForm({
                    title: item.title || '',
                    description: item.description || '',
                    modelIds: item.modelIds || [],
                });

                const prod = process.env.REACT_APP_PROD ? true : false;
                const API_URL = prod ? '' : 'http://localhost:8833';

                if (item.media && Array.isArray(item.media)) {
                    const mediaPreviews: MediaPreview[] = item.media.map((path: string) => {
                        const full = path.startsWith('http') ? path : `${API_URL}/${path}`;
                        const isVideo = /\.(mp4|webm|ogg)$/.test(full);
                        return { type: isVideo ? 'video' : 'image', src: full };
                    });

                    setPreviews(mediaPreviews);

                    if (item.preview) {
                        const previewUrl = item.preview.startsWith('http')
                            ? item.preview
                            : `${API_URL}/${item.preview}`;

                        const foundIndex = mediaPreviews.findIndex(mp => mp.src === previewUrl);
                        if (foundIndex !== -1) {
                            setPreviewIndex(foundIndex);
                            setSelectedIndex(foundIndex);
                        } else {
                            setPreviewIndex(0);
                            setSelectedIndex(0);
                        }
                    } else {
                        setPreviewIndex(0);
                        setSelectedIndex(0);
                    }
                }
            } catch (err) {
                console.error('Ошибка загрузки портфолио:', err);
            }
        };

        fetchItem();
    }, [id, isEdit]);

    const handleChange = (field: keyof typeof form, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const newFiles = Array.from(e.target.files);

        const newPreviews: MediaPreview[] = newFiles.map(file => {
            const type = file.type.startsWith('video') ? 'video' : 'image';
            return { type, src: URL.createObjectURL(file) };
        });

        setFiles(prev => [...prev, ...newFiles]);
        setPreviews(prev => [...prev, ...newPreviews]);
    };

    const handleRemoveFile = (index: number) => {
        const removed = previews[index];

        const prod = process.env.REACT_APP_PROD ? true : false;
        const baseUrl = prod ? '' : 'http://localhost:8833';

        const isOld = !files.some(f => removed.src.includes(f.name));
        if (isOld) {
            const relativePath = removed.src.replace(`${baseUrl}/`, '');
            setDeletedMedia(prev => [...prev, relativePath]);
        }

        setFiles(prev => prev.filter((_, i) => i !== index));
        setPreviews(prev => prev.filter((_, i) => i !== index));

        if (selectedIndex >= previews.length - 1) {
            setSelectedIndex(Math.max(0, selectedIndex - 1));
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (selectedIndex < previews.length - 1) {
                setSelectedIndex(selectedIndex + 1);
            }
        },
        onSwipedRight: () => {
            if (selectedIndex > 0) {
                setSelectedIndex(selectedIndex - 1);
            }
        },
        trackMouse: true,
    });

    const handleSubmit = async () => {
        if (!form.title.trim()) {
            setErrors({ title: 'Заголовок обязателен' });
            return;
        }

        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('description', form.description);

        form.modelIds.forEach(id => formData.append('modelIds', id));

        formData.append('previewIndex', String(previewIndex));

        files.forEach(file => formData.append('media', file));

        const prod = process.env.REACT_APP_PROD ? true : false;
        const baseUrl = prod ? '' : 'http://localhost:8833';

        deletedMedia.forEach(path => formData.append('deletedMedia', path));

        if (isEdit) {
            previews.forEach(({ src }) => {
                const isNew = files.some(file => src.includes(file.name));
                if (!isNew) {
                    const relative = src.replace(`${baseUrl}/`, '');
                    formData.append('existingMedia', relative);
                }
            });
        }

        try {
            if (isEdit) {
                await api.put(`/api/admin/portfolio/${id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                await api.post('/api/admin/portfolio', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }

            navigate('/portfolio');
        } catch (err) {
            console.error('Ошибка при сохранении портфолио:', err);
        }
    };

    return (
        <Box p={4}>
            <Button variant="outlined" onClick={() => navigate('/portfolio')} sx={{ mb: 2 }}>
                ← Назад
            </Button>

            <Typography variant="h5" gutterBottom>
                {isEdit ? 'Редактирование работы' : 'Создание работы портфолио'}
            </Typography>

            <Box sx={{ display: 'grid', gap: 2, maxWidth: 600 }}>
                <TextField
                    label="Заголовок"
                    value={form.title}
                    onChange={e => {
                        setErrors({});
                        handleChange('title', e.target.value);
                    }}
                    error={Boolean(errors.title)}
                    helperText={errors.title}
                    fullWidth
                    required
                />

                <BrandModelSelector
                    value={form.modelIds}
                    onChange={modelIds => handleChange('modelIds', modelIds)}
                />

                <TextField
                    label="Описание"
                    value={form.description}
                    onChange={e => handleChange('description', e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                />

                <Button variant="contained" component="label">
                    Загрузить фото / видео
                    <input type="file" hidden multiple onChange={handleFileChange} />
                </Button>

                <Alert severity="info" variant="filled">
                    Картинки и видео берём по той же логике, что и у дисков. Видео лучше не ставить
                    главным изображением.
                </Alert>

                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    width: 300,
                                    height: 200,
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    position: 'relative',
                                }}
                            >
                                {previews[selectedIndex].type === 'video' ? (
                                    <video
                                        key={previews[selectedIndex].src}
                                        src={previews[selectedIndex].src}
                                        controls
                                        autoPlay
                                        muted
                                        playsInline
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            border:
                                                selectedIndex === previewIndex
                                                    ? '3px solid #1976d2'
                                                    : '1px solid #ccc',
                                            borderRadius: 8,
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={previews[selectedIndex].src}
                                        alt={`preview-${selectedIndex}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            border:
                                                selectedIndex === previewIndex
                                                    ? '3px solid #1976d2'
                                                    : '1px solid #ccc',
                                            borderRadius: 8,
                                        }}
                                    />
                                )}

                                <IconButton
                                    onClick={() => handleRemoveFile(selectedIndex)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'white',
                                        color: 'red',
                                        '&:hover': { bgcolor: '#ffe5e5' },
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Box
                                sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}
                                {...swipeHandlers}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setSelectedIndex(prev => Math.max(0, prev - 1))
                                    }
                                    disabled={selectedIndex === 0}
                                >
                                    ←
                                </Button>
                                <Typography variant="caption">
                                    {selectedIndex + 1} / {previews.length}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setSelectedIndex(prev =>
                                            Math.min(previews.length - 1, prev + 1)
                                        )
                                    }
                                    disabled={selectedIndex === previews.length - 1}
                                >
                                    →
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}

                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" mt={2} gap={1}>
                        {previews.map((item, i) => (
                            <Box key={i} position="relative">
                                {item.type === 'video' ? (
                                    <video
                                        src={item.src}
                                        onClick={() => setSelectedIndex(i)}
                                        style={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            border:
                                                i === selectedIndex ? '2px solid blue' : '1px solid #ccc',
                                            cursor: 'pointer',
                                        }}
                                        muted
                                        playsInline
                                    />
                                ) : (
                                    <img
                                        src={item.src}
                                        alt={`thumb-${i}`}
                                        onClick={() => setSelectedIndex(i)}
                                        style={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            border:
                                                i === selectedIndex ? '2px solid blue' : '1px solid #ccc',
                                            cursor: 'pointer',
                                        }}
                                    />
                                )}
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFile(i)}
                                    sx={{ position: 'absolute', top: -10, right: -10 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}

                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" gap={2} alignItems="center" mt={1}>
                        <Typography variant="caption">
                            {selectedIndex + 1} / {previews.length}
                        </Typography>

                        <Button
                            variant={selectedIndex === previewIndex ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setPreviewIndex(selectedIndex)}
                        >
                            {selectedIndex === previewIndex
                                ? 'Главное изображение'
                                : 'Сделать главным'}
                        </Button>
                    </Box>
                )}

                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    Сохранить
                </Button>
            </Box>
        </Box>
    );
};

export default PortfolioDetailPage;
